import React from "react";
import { storage } from "../../utils/firebase";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { IAuth } from "../../types/authTypes";
import { authSelector } from "../../utils/selectors";
import styles from "./UploadPDF.module.scss";
import { PDFsPreview } from "./PDFPreview";


export type PreviewPDF = {
  url: string;
  name: string;
  processing: boolean;
  sizeBytes?: number;
};

type InnerProps = {
  auth: IAuth;
};

type Props = {
  pdfs: PreviewPDF[];
  updatePDFList: React.Dispatch<React.SetStateAction<PreviewPDF[]>>;
} & InnerProps;


const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const ACCEPTED_EXTENSIONS = [".pdf"];


const UploadPDF: React.FC<Props> = ({ pdfs, updatePDFList, auth }) => {
  const intl = useIntl();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [loading, setLoading] = React.useState(0);
  const [processingMessage, setProcessingMessage] = React.useState<string>("");
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [isDragOver, setIsDragOver] = React.useState<boolean>(false);


  const uploadFile = (file: File) => {
    if (isProcessing) return;

    setError("");

    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setError(
        intl.formatMessage({
          id: "site.pdfUpload.invalidType",
          defaultMessage: "Only PDF files are accepted.",
        })
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(
        intl.formatMessage({
          id: "site.pdfUpload.fileSizeTooBig",
          defaultMessage: "File size is too big. Max 20 MB.",
        })
      );
      return;
    }

    setIsProcessing(true);

    const storageRef = ref(
      storage,
      `patterns/pdfs/${auth.user && auth.user._id}/${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setLoading(progress);
        setProcessingMessage(
          intl.formatMessage(
            { id: "site.pdfUpload.uploading", defaultMessage: "Uploading... {progress}%" },
            { progress }
          )
        );
      },
      (err) => {
        console.error(err.message);
        setError(err.message);
        setLoading(0);
        setProcessingMessage("");
        setIsProcessing(false);
      },
      async () => {
        setProcessingMessage(
          intl.formatMessage({
            id: "site.pdfUpload.processing",
            defaultMessage: "Processing...",
          })
        );

        const url = await getDownloadURL(uploadTask.snapshot.ref);

        updatePDFList((prev: PreviewPDF[]) => {
          if (prev.some((p) => p.url === url)) return prev;

          return [
            ...prev,
            {
              url,
              name: file.name,
              processing: false,
              sizeBytes: file.size,
            },
          ];
        });

        setLoading(100);
        setIsProcessing(false);
        setProcessingMessage("");

        if (inputRef.current) inputRef.current.value = "";
      }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };


  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };


  const onDelete = async (pdf: PreviewPDF) => {
    const pdfRef = ref(
      storage,
      `patterns/pdfs/${auth.user?._id}/${pdf.name}`
    );
    try {
      await Promise.allSettled([deleteObject(pdfRef)]);
    } catch (err: any) {
      console.error(err.message);
    } finally {
      updatePDFList(pdfs.filter((p) => p !== pdf));
    }
  };


  return (
    <>
      <div
        className={`${styles.dropzone} ${isDragOver ? styles.dragOver : ""} ${isProcessing ? styles.disabled : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isProcessing && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && !isProcessing && inputRef.current?.click()}
        aria-label="Upload PDF"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        <span className={styles.uploadIcon}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </span>
        <p className={styles.dropzoneLabel}>
          {intl.formatMessage({
            id: "site.pdfUpload.chooseFile",
            defaultMessage: "Choose PDF or drag & drop here",
          })}
        </p>
        <p className={styles.dropzoneHint}>
          {intl.formatMessage({
            id: "site.pdfUpload.uploadLabel",
            defaultMessage: "Max file size: 20 MB · PDF only",
          })}
        </p>
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}

      {processingMessage && (
        <div
          className={`${styles.processingMessage} ${isProcessing ? styles.processing : styles.idle}`}
        >
          {processingMessage}
        </div>
      )}

      <PDFsPreview pdfs={pdfs} onDelete={onDelete} loading={loading} />
    </>
  );
};


const mapState = (state: InnerProps) => ({
  auth: authSelector(state),
});

export default connect(mapState)(UploadPDF);